import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    Pressable,
    TextInput,
    Platform,
    RefreshControl,
    ActivityIndicator,
    Switch,
} from 'react-native';
import { DatePickerModal } from 'react-native-paper-dates';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import type { RootStackParamList } from '../navigation/stackTypes';
import { isSuccessful } from '../services/api-requests';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { BottomSheetModal, BottomSheetScrollView } from '../components/BottomSheet';
import type { BottomSheetModalRef } from '../components/BottomSheet';
import { getPricingCodes, createPricingCode } from '../services/pricing-code-service';
import type { PricingCode, CreatePricingCodePayload } from '../models/pricing-code-model';
import type { DiscountType, GroupScope } from '../models/pricing-group-model';
import { formatDiscountLabel, DISCOUNT_BADGE_COLORS } from '../models/pricing-group-model';
import { showThemedAlert } from '../components/common/ThemedAlert';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'PricingCodesManagement'>;

const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const pick = () => chars.charAt(Math.floor(Math.random() * chars.length));
    return `${pick()}${pick()}${pick()}${pick()}-${pick()}${pick()}${pick()}${pick()}`;
};

const getCodeStatus = (code: PricingCode) => {
    if (code.is_revoked) return { label: 'Revoked', color: '#EF4444' };
    if (code.is_expired || (code.expires_at && new Date(code.expires_at) < new Date()))
        return { label: 'Expired', color: '#F59E0B' };
    return { label: 'Active', color: '#10B981' };
};

const DISCOUNT_TYPES: { key: DiscountType; label: string }[] = [
    { key: 'free', label: 'Free' },
    { key: 'percentage', label: 'Percentage' },
    { key: 'flat_rate', label: 'Flat Rate' },
];

const SCOPE_OPTIONS: { key: GroupScope; label: string }[] = [
    { key: 'all_host_chargers', label: 'All My Chargers' },
    { key: 'specific_chargers', label: 'Select Chargers' },
];

const PricingCodesManagement = () => {
    const navigation = useNavigation<NavigationProp>();
    const isFocused = useIsFocused();
    const myChargers = useSelector((state: RootState) => state.chargers.myChargers);

    const [codes, setCodes] = useState<PricingCode[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showRevoked, setShowRevoked] = useState(false);
    // Form state
    const [codeString, setCodeString] = useState('');
    const [codeName, setCodeName] = useState('');
    const [discountType, setDiscountType] = useState<DiscountType>('free');
    const [discountValue, setDiscountValue] = useState('');
    const [scope, setScope] = useState<GroupScope>('all_host_chargers');
    const [selectedChargerIds, setSelectedChargerIds] = useState<string[]>([]);
    const [maxRedemptions, setMaxRedemptions] = useState('');
    const [hasMaxRedemptions, setHasMaxRedemptions] = useState(false);
    const [expiresAt, setExpiresAt] = useState('');
    const [hasExpiration, setHasExpiration] = useState(false);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [creating, setCreating] = useState(false);

    const bottomSheetRef = useRef<BottomSheetModalRef>(null);
    const snapPoints = useMemo(() => ['92%'], []);

    const fetchCodes = useCallback(async () => {
        try {
            const response = await getPricingCodes();
            if (isSuccessful(response.status)) {
                setCodes(response.payload?.codes ?? []);
            }
        } catch (err) {
            console.error('Failed to fetch pricing codes:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchCodes();
    }, [fetchCodes]);

    // Refresh when screen regains focus (e.g. after revoking from detail screen)
    useEffect(() => {
        if (isFocused) {
            fetchCodes();
        }
    }, [isFocused]);

    const onRefresh = useCallback(() => {
        setRefreshing(true);
        fetchCodes();
    }, [fetchCodes]);

    const resetForm = () => {
        setCodeString('');
        setCodeName('');
        setDiscountType('free');
        setDiscountValue('');
        setScope('all_host_chargers');
        setSelectedChargerIds([]);
        setMaxRedemptions('');
        setHasMaxRedemptions(false);
        setExpiresAt('');
        setHasExpiration(false);
    };

    const openCreateModal = () => {
        resetForm();
        bottomSheetRef.current?.present();
    };

    const toggleCharger = (chargerId: string) => {
        setSelectedChargerIds((prev) =>
            prev.includes(chargerId)
                ? prev.filter((id) => id !== chargerId)
                : [...prev, chargerId]
        );
    };

    const handleDiscountValueChange = useCallback((text: string) => {
        if (discountType === 'percentage') {
            const cleaned = text.replace(/[^0-9]/g, '');
            const num = parseInt(cleaned, 10);
            if (cleaned === '' || (num >= 0 && num <= 100)) {
                setDiscountValue(cleaned);
            }
        } else {
            setDiscountValue(text);
        }
    }, [discountType]);

    const handleCreate = async () => {
        const trimmedCode = codeString.trim().toUpperCase();
        if (!trimmedCode) {
            showThemedAlert('Validation', 'Please enter or generate a code.');
            return;
        }
        if (!codeName.trim()) {
            showThemedAlert('Validation', 'Please enter a code name.');
            return;
        }
        if (discountType === 'percentage') {
            const parsed = parseInt(discountValue, 10);
            if (isNaN(parsed) || parsed < 1 || parsed > 100) {
                showThemedAlert('Validation', 'Please enter a whole number between 1 and 100.');
                return;
            }
        } else if (discountType !== 'free' && (!discountValue || parseFloat(discountValue) <= 0)) {
            showThemedAlert('Validation', 'Please enter a valid discount value.');
            return;
        }
        if (scope === 'specific_chargers' && selectedChargerIds.length === 0) {
            showThemedAlert('Validation', 'Please select at least one charger.');
            return;
        }

        setCreating(true);
        try {
            const payload: CreatePricingCodePayload = {
                code: trimmedCode,
                name: codeName.trim(),
                discount_type: discountType,
                discount_value: discountType === 'free' ? 0
                    : discountType === 'percentage' ? parseInt(discountValue, 10)
                    : parseFloat(discountValue),
                scope,
                ...(scope === 'specific_chargers' && { charger_ids: selectedChargerIds }),
                ...(maxRedemptions.trim() && { max_redemptions: parseInt(maxRedemptions, 10) }),
                ...(expiresAt.trim() && { expires_at: expiresAt.trim() }),
            };

            const response = await createPricingCode(payload);
            if (isSuccessful(response.status)) {
                bottomSheetRef.current?.dismiss();
                fetchCodes();
                showThemedAlert('Success', 'Pricing code created successfully.');
            } else {
                showThemedAlert('Error', 'Failed to create pricing code. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create pricing code:', err);
            showThemedAlert('Error', 'An unexpected error occurred.');
        } finally {
            setCreating(false);
        }
    };

    const hasRevokedCodes = codes.some((c) => c.is_revoked);
    const displayedCodes = showRevoked ? codes : codes.filter((c) => !c.is_revoked);

    const renderCodeCard = (code: PricingCode) => {
        const status = getCodeStatus(code);
        const badgeColor = DISCOUNT_BADGE_COLORS[code.discount_type];
        const redemptionLabel = code.max_redemptions
            ? `${code.total_redemptions} / ${code.max_redemptions}`
            : `${code.total_redemptions} / ∞`;

        return (
            <Pressable
                key={code.id}
                style={({ pressed }) => [styles.card, { opacity: pressed ? 0.85 : 1 }]}
                onPress={() => navigation.navigate('PricingCodeDetail', { codeId: code.id })}
            >
                <View style={styles.cardTopRow}>
                    <View style={styles.couponBadge}>
                        <Ionicons name="ticket-outline" size={14} color="#40BFB4" style={{ marginRight: 6 }} />
                        <Text style={styles.couponBadgeText}>{code.code}</Text>
                    </View>
                    <View style={[styles.statusBadge, { backgroundColor: status.color + '20' }]}>
                        <View style={[styles.statusDot, { backgroundColor: status.color }]} />
                        <Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text>
                    </View>
                </View>

                <Text style={styles.cardTitle}>{code.name}</Text>

                <View style={styles.cardMetaRow}>
                    <View style={[styles.discountBadge, { backgroundColor: badgeColor + '20' }]}>
                        <Text style={[styles.discountBadgeText, { color: badgeColor }]}>
                            {formatDiscountLabel(code.discount_type, code.discount_value)}
                        </Text>
                    </View>

                    <View style={styles.metaItem}>
                        <MaterialIcons name="people-outline" size={14} color="#8D8E90" />
                        <Text style={styles.metaText}>{redemptionLabel}</Text>
                    </View>
                </View>

                <View style={styles.cardBottomRow}>
                    <Text style={styles.scopeText}>
                        {code.scope === 'all_host_chargers' ? 'All Chargers' : `${code.charger_count} Charger${code.charger_count !== 1 ? 's' : ''}`}
                    </Text>
                    <MaterialIcons name="chevron-right" size={20} color="#8D8E90" />
                </View>
            </Pressable>
        );
    };

    const renderEmpty = () => (
        <View style={styles.emptyContainer}>
            <Ionicons name="ticket-outline" size={56} color="#8D8E90" />
            <Text style={styles.emptyTitle}>No Pricing Codes</Text>
            <Text style={styles.emptySubtitle}>
                Create a pricing code to offer discounts via shareable coupon codes.
            </Text>
        </View>
    );

    return (
        <SafeAreaView style={styles.safeArea} edges={['top']}>
            <Header headerText="Pricing Codes" />

            {loading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#40BFB4" />
                </View>
            ) : (
                <>
                <View style={styles.topBar}>
                    <Text style={styles.codeCount}>
                        {codes.length} {codes.length === 1 ? 'code' : 'codes'}
                    </Text>
                    <Pressable
                        style={({ pressed }) => [styles.addButton, { opacity: pressed ? 0.85 : 1 }]}
                        onPress={openCreateModal}
                    >
                        <MaterialIcons name="add" size={18} color="#FFFFFF" />
                        <Text style={styles.addButtonText}>Create</Text>
                    </Pressable>
                </View>
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    refreshControl={
                        <RefreshControl
                            refreshing={refreshing}
                            onRefresh={onRefresh}
                            tintColor="#40BFB4"
                        />
                    }
                >
                    {/* Revoked filter toggle */}
                    {hasRevokedCodes && (
                        <View style={styles.filterRow}>
                            <Text style={styles.filterLabel}>Show Revoked Codes</Text>
                            <Switch
                                value={showRevoked}
                                onValueChange={setShowRevoked}
                                trackColor={{ false: '#3A3D42', true: '#39BEB750' }}
                                thumbColor={showRevoked ? '#39BEB7' : '#8D8E90'}
                            />
                        </View>
                    )}

                    {displayedCodes.length === 0
                        ? (codes.length === 0 ? renderEmpty() : (
                            <View style={styles.emptyContainer}>
                                <Text style={styles.emptySubtitle}>
                                    All codes are revoked. Toggle "Show Revoked Codes" to view them.
                                </Text>
                            </View>
                        ))
                        : displayedCodes.map(renderCodeCard)}
                </ScrollView>
                </>
            )}

            {/* Create Code Bottom Sheet */}
            <BottomSheetModal
                ref={bottomSheetRef}
                index={0}
                snapPoints={snapPoints}
                enablePanDownToClose
                backgroundStyle={styles.sheetBackground}
                handleIndicatorStyle={styles.sheetHandle}
                style={Platform.OS === 'web' ? styles.sheetWeb : undefined}
            >
                <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>Create Pricing Code</Text>
                    <Pressable onPress={() => bottomSheetRef.current?.dismiss()} hitSlop={12}>
                        <MaterialIcons name="close" size={24} color="#8D8E90" />
                    </Pressable>
                </View>

                <BottomSheetScrollView
                    contentContainerStyle={styles.modalScrollContent}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                >
                            {/* Code Input Row */}
                            <Text style={styles.label}>Code</Text>
                            <View style={styles.codeInputRow}>
                                <TextInput
                                    style={[styles.input, styles.codeInput]}
                                    value={codeString}
                                    onChangeText={(text) => setCodeString(text.toUpperCase())}
                                    placeholder="e.g. SUMMER25"
                                    placeholderTextColor="#555"
                                    autoCapitalize="characters"
                                    maxLength={20}
                                />
                                <Pressable
                                    style={({ pressed }) => [
                                        styles.generateButton,
                                        { opacity: pressed ? 0.7 : 1 },
                                    ]}
                                    onPress={() => setCodeString(generateCode())}
                                >
                                    <Ionicons name="dice-outline" size={20} color="#FFFFFF" />
                                </Pressable>
                            </View>

                            {/* Code Name */}
                            <Text style={styles.label}>Code Name</Text>
                            <TextInput
                                style={styles.input}
                                value={codeName}
                                onChangeText={setCodeName}
                                placeholder="e.g. Summer Promotion"
                                placeholderTextColor="#555"
                            />

                            {/* Discount Type */}
                            <Text style={styles.label}>Discount Type</Text>
                            <View style={styles.chipRow}>
                                {DISCOUNT_TYPES.map((dt) => {
                                    const selected = discountType === dt.key;
                                    return (
                                        <Pressable
                                            key={dt.key}
                                            style={[styles.chip, selected && styles.chipSelected]}
                                            onPress={() => {
                                                setDiscountType(dt.key);
                                                if (dt.key === 'free') setDiscountValue('');
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    selected && styles.chipTextSelected,
                                                ]}
                                            >
                                                {dt.label}
                                            </Text>
                                        </Pressable>
                                    );
                                })}
                            </View>

                            {/* Discount Value (conditional) */}
                            {discountType !== 'free' && (
                                <>
                                    <Text style={styles.label}>
                                        {discountType === 'percentage'
                                            ? 'Discount Percentage'
                                            : 'Flat Rate'}
                                    </Text>
                                    <TextInput
                                        style={styles.input}
                                        value={discountValue}
                                        onChangeText={handleDiscountValueChange}
                                        placeholder={
                                            discountType === 'percentage' ? 'e.g. 25' : 'e.g. 0.150'
                                        }
                                        placeholderTextColor="#555"
                                        keyboardType={discountType === 'percentage' ? 'number-pad' : 'decimal-pad'}
                                        maxLength={discountType === 'percentage' ? 3 : undefined}
                                    />
                                </>
                            )}

                            {/* Scope */}
                            <Text style={styles.label}>Scope</Text>
                            <View style={styles.chipRow}>
                                {SCOPE_OPTIONS.map((opt) => {
                                    const selected = scope === opt.key;
                                    return (
                                        <Pressable
                                            key={opt.key}
                                            style={[styles.chip, selected && styles.chipSelected]}
                                            onPress={() => {
                                                setScope(opt.key);
                                                if (opt.key === 'all_host_chargers')
                                                    setSelectedChargerIds([]);
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    selected && styles.chipTextSelected,
                                                ]}
                                            >
                                                {opt.label}
                                            </Text>
                                        </Pressable>
                                    );
                                })}
                            </View>

                            {/* Charger Checklist */}
                            {scope === 'specific_chargers' && (
                                <View style={styles.chargerListContainer}>
                                    <ScrollView
                                        style={styles.chargerScroll}
                                        nestedScrollEnabled
                                        showsVerticalScrollIndicator
                                    >
                                        {myChargers.length === 0 ? (
                                            <Text style={styles.noChargersText}>
                                                No chargers registered.
                                            </Text>
                                        ) : (
                                            myChargers.map((charger) => {
                                                const isChecked = selectedChargerIds.includes(
                                                    charger.uuid ?? ''
                                                );
                                                return (
                                                    <Pressable
                                                        key={charger.uuid}
                                                        style={styles.chargerRow}
                                                        onPress={() =>
                                                            toggleCharger(charger.uuid ?? '')
                                                        }
                                                    >
                                                        <MaterialIcons
                                                            name={
                                                                isChecked
                                                                    ? 'check-box'
                                                                    : 'check-box-outline-blank'
                                                            }
                                                            size={22}
                                                            color={
                                                                isChecked ? '#40BFB4' : '#8D8E90'
                                                            }
                                                        />
                                                        <Text style={styles.chargerName}>
                                                            {charger.name || charger.unit_serial}
                                                        </Text>
                                                    </Pressable>
                                                );
                                            })
                                        )}
                                    </ScrollView>
                                </View>
                            )}

                            {/* Max Redemptions */}
                            <View style={styles.toggleRow}>
                                <Text style={[styles.label, { marginTop: 0, marginBottom: 0 }]}>Redemption Limit</Text>
                                <Switch
                                    value={hasMaxRedemptions}
                                    onValueChange={(val) => {
                                        setHasMaxRedemptions(val);
                                        if (!val) setMaxRedemptions('');
                                    }}
                                    trackColor={{ false: '#3A3D42', true: '#39BEB750' }}
                                    thumbColor={hasMaxRedemptions ? '#39BEB7' : '#8D8E90'}
                                />
                            </View>
                            {hasMaxRedemptions ? (
                                <TextInput
                                    style={styles.input}
                                    value={maxRedemptions}
                                    onChangeText={setMaxRedemptions}
                                    placeholder="Enter max number of redemptions"
                                    placeholderTextColor="#555"
                                    keyboardType="number-pad"
                                />
                            ) : (
                                <Text style={styles.toggleHint}>Unlimited redemptions</Text>
                            )}

                            {/* Expiration Date */}
                            <View style={styles.toggleRow}>
                                <Text style={[styles.label, { marginTop: 0, marginBottom: 0 }]}>Expiration Date</Text>
                                <Switch
                                    value={hasExpiration}
                                    onValueChange={(val) => {
                                        setHasExpiration(val);
                                        if (!val) setExpiresAt('');
                                    }}
                                    trackColor={{ false: '#3A3D42', true: '#39BEB750' }}
                                    thumbColor={hasExpiration ? '#39BEB7' : '#8D8E90'}
                                />
                            </View>
                            {hasExpiration ? (
                                <Pressable
                                    style={styles.dateInput}
                                    onPress={() => setDatePickerOpen(true)}
                                >
                                    <Text style={expiresAt ? styles.dateInputText : styles.dateInputPlaceholder}>
                                        {expiresAt || 'Tap to select date'}
                                    </Text>
                                    <MaterialIcons name="calendar-today" size={18} color="#8D8E90" />
                                </Pressable>
                            ) : (
                                <Text style={styles.toggleHint}>No expiration</Text>
                            )}
                            <DatePickerModal
                                locale="en"
                                mode="single"
                                visible={datePickerOpen}
                                onDismiss={() => setDatePickerOpen(false)}
                                date={expiresAt ? new Date(expiresAt) : undefined}
                                onConfirm={(params) => {
                                    setDatePickerOpen(false);
                                    if (params.date) {
                                        const d = params.date;
                                        const yyyy = d.getFullYear();
                                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                                        const dd = String(d.getDate()).padStart(2, '0');
                                        setExpiresAt(`${yyyy}-${mm}-${dd}`);
                                    }
                                }}
                                validRange={{ startDate: new Date() }}
                            />

                            {/* Create Button */}
                            <View style={styles.createButtonContainer}>
                                <LoadingButton
                                    onPress={handleCreate}
                                    backgroundColor="#39BEB7"
                                    isLoading={creating}
                                    disabled={creating}
                                    style={styles.createButton}
                                >
                                    Create Code
                                </LoadingButton>
                            </View>
                </BottomSheetScrollView>
            </BottomSheetModal>
        </SafeAreaView>
    );
};

export default PricingCodesManagement;

const styles = StyleSheet.create({
    safeArea: {
        flex: 1,
        backgroundColor: '#1A1D21',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: 16,
        paddingBottom: 32,
    },

    // Filter row
    filterRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#292D33',
        borderRadius: 12,
        paddingHorizontal: 16,
        paddingVertical: 10,
        marginBottom: 14,
    },
    filterLabel: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 14,
        fontWeight: '600',
        color: '#FFFFFF',
    },

    // Top bar
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingBottom: 12,
    },
    codeCount: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 14,
        color: '#8D8E90',
    },
    addButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#39BEB7',
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        gap: 4,
    },
    addButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
        fontFamily: 'PlusJakartaSans-Regular',
    },

    // Code card
    card: {
        backgroundColor: '#292D33',
        borderRadius: 14,
        padding: 16,
        marginBottom: 12,
    },
    cardTopRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    couponBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#40BFB415',
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#40BFB430',
        borderStyle: 'dashed',
    },
    couponBadgeText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 13,
        fontWeight: '700',
        color: '#40BFB4',
        letterSpacing: 1.5,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 10,
    },
    statusDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        marginRight: 5,
    },
    statusText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 11,
        fontWeight: '600',
    },
    cardTitle: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 16,
        fontWeight: '600',
        color: '#FFFFFF',
        marginBottom: 10,
    },
    cardMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 10,
        gap: 10,
    },
    discountBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
    },
    discountBadgeText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 12,
        fontWeight: '600',
    },
    metaItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    metaText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 12,
        color: '#8D8E90',
    },
    cardBottomRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#3A3F47',
        paddingTop: 10,
    },
    scopeText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 12,
        color: '#8D8E90',
    },

    // Empty state
    emptyContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 80,
        paddingHorizontal: 32,
    },
    emptyTitle: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 18,
        fontWeight: '600',
        color: '#FFFFFF',
        marginTop: 16,
    },
    emptySubtitle: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 14,
        color: '#8D8E90',
        textAlign: 'center',
        marginTop: 8,
        lineHeight: 20,
    },

    // Bottom Sheet
    sheetBackground: {
        backgroundColor: '#1A1D21',
        borderRadius: 20,
    },
    sheetHandle: {
        backgroundColor: '#555',
        width: 40,
        height: 4,
    },
    sheetWeb: {
        maxWidth: 430,
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#3A3F47',
    },
    modalTitle: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 18,
        fontWeight: '600',
        color: '#FFFFFF',
    },
    modalScrollContent: {
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 24,
    },
    label: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 13,
        fontWeight: '600',
        color: '#8D8E90',
        marginBottom: 6,
        marginTop: 14,
    },
    input: {
        backgroundColor: '#292D33',
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        fontSize: 15,
        color: '#FFFFFF',
        fontFamily: 'PlusJakartaSans-Regular',
        marginBottom: 4,
    },
    codeInputRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    codeInput: {
        flex: 1,
        marginBottom: 0,
        marginRight: 8,
        letterSpacing: 1.5,
        fontWeight: '600',
    },
    generateButton: {
        backgroundColor: '#292D33',
        borderRadius: 10,
        width: 46,
        height: 46,
        alignItems: 'center',
        justifyContent: 'center',
    },
    chipRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    chip: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: '#292D33',
        borderWidth: 1,
        borderColor: '#3A3F47',
    },
    chipSelected: {
        backgroundColor: '#39BEB720',
        borderColor: '#39BEB7',
    },
    chipText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 13,
        color: '#8D8E90',
    },
    chipTextSelected: {
        color: '#39BEB7',
        fontWeight: '600',
    },
    chargerListContainer: {
        backgroundColor: '#292D33',
        borderRadius: 10,
        marginTop: 8,
        overflow: 'hidden',
    },
    chargerScroll: {
        maxHeight: 180,
        paddingVertical: 4,
    },
    chargerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
    },
    chargerName: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 14,
        color: '#FFFFFF',
        marginLeft: 10,
    },
    noChargersText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 13,
        color: '#8D8E90',
        padding: 14,
        textAlign: 'center',
    },
    createButtonContainer: {
        marginTop: 20,
    },
    createButton: {
        borderRadius: 24,
    },
    toggleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 14,
        marginBottom: 6,
    },
    toggleHint: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 13,
        color: '#555',
        fontStyle: 'italic',
        marginBottom: 4,
    },
    dateInput: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#292D33',
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        marginBottom: 4,
    },
    dateInputText: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 15,
        color: '#FFFFFF',
    },
    dateInputPlaceholder: {
        fontFamily: 'PlusJakartaSans-Regular',
        fontSize: 15,
        color: '#555',
    },
});
